import type { InlineQueryResult } from "@octopusdeploy/octopus-react-client";
import type { ActivityStatus, DeploymentManifestDiffResource, KubernetesResourceManifestDiffResource, KubernetesResourceManifestSummaryResource } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import type { ManifestDiffNavigationTree } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestNavigationTree";
import type { FilteredItemCounts, ManifestDiffListItem } from "~/areas/tasks/components/Task/Kubernetes/Manifests/ManifestViewerList";
import { applyFilterToKubernetesResourceManifestSummary } from "~/areas/tasks/components/Task/Kubernetes/Manifests/applyFilterToKubernetesResourceManifestSummary";
import type { KubernetesStepWithStatus } from "~/areas/tasks/components/Task/Kubernetes/Manifests/useKubernetesStepsWithStatus";
import { flattenDiffsResourceHierarchy } from "~/areas/tasks/components/Task/Kubernetes/flattenDiffsResourceHierarchy";
import { getManifestSummaryForDeployment } from "~/areas/tasks/components/Task/Kubernetes/getManifestSummaryForDeployment";
import StringHelper from "~/utils/StringHelper";
import type { KubernetesDetailsFilter } from "../KubernetesDetailsLayout";
import { hasNoFilters } from "../KubernetesDetailsLayout";
import type { DeploymentDiffSelection, DiffQueryResult } from "./KubernetesAppliedManifests";
export function useManifestDiffsTreeAndOrderedListItems(diffQuery: DiffQueryResult | undefined, filter: KubernetesDetailsFilter, hideUnchangedManifests: boolean, stepsWithStatus: KubernetesStepWithStatus[]): InlineQueryResult<[
    ManifestDiffNavigationTree,
    ManifestDiffListItem[],
    FilteredItemCounts
], null> {
    const diffSelection = diffQuery?.selection;
    const filteredResources = useMemo(() => {
        if (!diffQuery?.result?.Resources) {
            return [];
        }
        //if there are no filters, just return the full array
        if (hasNoFilters(filter)) {
            return diffQuery.result.Resources;
        }
        return diffQuery.result.Resources.map((deploymentDiffResource) => ({
            ...deploymentDiffResource,
            StepDiffs: deploymentDiffResource.StepDiffs.map((stepDiff) => ({
                ...stepDiff,
                DiffResources: stepDiff.DiffResources.filter((diffResource) => {
                    const krm = getManifestSummaryForDeployment(diffResource, deploymentDiffResource.DeploymentId);
                    return krm && applyFilterToKubernetesResourceManifestSummary(krm, filter);
                }),
            })),
        }));
    }, [filter, diffQuery?.result?.Resources]);
    const diffNavigationTree: ManifestDiffNavigationTree = useMemo(() => {
        if (filteredResources.length === 0 || !diffSelection?.fromDeploymentId) {
            return { deploymentTrees: [] };
        }
        return convertDiffResultToNavigationTrees(filteredResources, stepsWithStatus, diffSelection, hideUnchangedManifests);
    }, [diffSelection, filteredResources, hideUnchangedManifests, stepsWithStatus]);
    const orderedDiffs: ManifestDiffListItem[] = useMemo(() => {
        if (diffNavigationTree.deploymentTrees.length === 0) {
            return [];
        }
        return diffNavigationTree.deploymentTrees.flatMap((navTree) => navTree.stepItems.flatMap((stepItem) => stepItem.targetItems
            .flatMap((targetItem) => targetItem.namespaceItems.flatMap((namespaceItem) => namespaceItem.manifestItems))
            .map((manifestItem) => ({
            key: `${manifestItem.resource.Id}-${manifestItem.diffResource.DiffResult}`,
            krm: manifestItem.resource,
            diffResource: manifestItem.diffResource,
            resourceStatus: manifestItem.objectStatus,
            stepStatus: stepItem.stepStatus,
        }))));
    }, [diffNavigationTree]);
    const manifestCounts: FilteredItemCounts = useMemo(() => ({ total: countDiffResources(diffQuery?.result?.Resources), filteredTotal: countDiffResources(filteredResources) }), [diffQuery?.result?.Resources, filteredResources]);
    if (diffQuery?.isLoading) {
        return { result: null, isLoading: true, error: null };
    }
    if (diffQuery?.error) {
        return { result: null, isLoading: false, error: diffQuery.error };
    }
    return { result: [diffNavigationTree, orderedDiffs, manifestCounts], isLoading: false, error: null };
}
function countDiffResources(resources: DeploymentManifestDiffResource[] | undefined): number {
    return flattenDiffsResourceHierarchy(resources).length;
}
function convertDiffResultToNavigationTrees(filteredResources: DeploymentManifestDiffResource[], stepsWithStatus: KubernetesStepWithStatus[], diffSelection: DeploymentDiffSelection, hideUnchangedManifests: boolean): ManifestDiffNavigationTree {
    return {
        //we sort the array so the fromDeployment is last (if it exists) (sort is ascending)
        deploymentTrees: filteredResources
            .sort((d) => (d.DeploymentId === diffSelection.fromDeploymentId ? 1 : -1))
            .map((deploymentDiffResource) => ({
            executionId: deploymentDiffResource.DeploymentId,
            name: deploymentDiffResource.Name,
            isCurrentExecution: deploymentDiffResource.DeploymentId !== diffSelection.fromDeploymentId,
            stepItems: deploymentDiffResource.StepDiffs.map((step) => {
                const perStepDiffs = step.DiffResources.map((v) => ({
                    diffResource: v,
                    krm: getManifestSummaryForDeployment(v, deploymentDiffResource.DeploymentId),
                })).filter((v): v is {
                    diffResource: KubernetesResourceManifestDiffResource;
                    krm: KubernetesResourceManifestSummaryResource;
                } => !!v.krm);
                //get the unique machine ids
                const machineIds = [...new Set(perStepDiffs.map(({ krm }) => krm.MachineId))];
                //get all the unique namespaces
                const namespaces = [...new Set(perStepDiffs.map(({ krm }) => krm.KubernetesObjectNamespace))];
                let stepStatus: ActivityStatus | undefined = undefined;
                //we only look up the step status for the current deployment
                if (deploymentDiffResource.DeploymentId === diffSelection.toDeploymentId) {
                    const stepWithStatus = stepsWithStatus.find((s) => s.actionId === step.ActionId);
                    stepStatus = stepWithStatus?.status;
                }
                stepsWithStatus.find((s) => s.actionId === step.ActionId);
                return {
                    stepName: step.StepName,
                    stepStatus,
                    actionId: step.ActionId,
                    targetItems: machineIds
                        .map((machineId) => {
                        const perMachineResources = perStepDiffs.filter(({ krm }) => krm.MachineId === machineId);
                        return {
                            machineId,
                            targetName: perMachineResources[0]?.krm.MachineName || "", //all the machines will have the same name, so just pick the first one
                            namespaceItems: namespaces
                                .map((ns) => {
                                const perNamespaceResources = perMachineResources.filter(({ krm }) => krm.KubernetesObjectNamespace === ns);
                                return {
                                    namespace: !ns || StringHelper.isNullOrWhiteSpace(ns) ? "Cluster-scoped" : ns,
                                    manifestItems: perNamespaceResources
                                        .map(({ diffResource, krm }) => ({
                                        resource: krm,
                                        name: krm.KubernetesObjectName,
                                        kind: krm.KubernetesResourceKind,
                                        objectStatus: krm.ResourceStatus,
                                        diffResource,
                                    }))
                                        //if hide unchanged is true and this is an unchanged resource, don't include it
                                        .filter((x) => !(hideUnchangedManifests && x.diffResource.DiffResult === "Unchanged")),
                                };
                            })
                                .filter((t) => t.manifestItems.length > 0),
                        };
                    })
                        .filter((t) => t.namespaceItems.length > 0),
                };
            }).filter((t) => t.targetItems.length > 0),
        }))
            .filter((t) => t.stepItems.length > 0),
    };
}
